import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'
import { useToggle } from 'react-use'

import { useUncontrolled } from '../../hooks/useUncontrolled'
import { Box } from '../Box'
import type { CascadeOption, CascadeValue } from '../CascadeList'
import { CascadeList } from '../CascadeList'
import { Flex } from '../Flex'
import { IconFont } from '../IconFont'
import { Popover } from '../Popover'
import { Text } from '../Text'
import { Tooltip } from '../Tooltip'
import { useStyles } from './CascadeSelect.styles'
import type { CascadeSelectProps } from './CascadeSelect.types'

export const CascadeSelect = React.forwardRef<HTMLDivElement, CascadeSelectProps>(
    (
        {
            data,
            options = [],
            withinPortal,
            className,
            style,
            clearable = false,
            downIcon = 'ArrowDownSmall',
            styles,
            multiple,
            columnWidth,
            searchable,
            listProps,
            selectionMode,
            popoverWidth,
            size = 'md',
            focusOutLine = true,
            disabled,
            placeholder = '请选择',
            allSelected,
            onChange,
            onClear
        },
        ref
    ) => {
        const [opened, toggle] = useToggle(false)

        const [_value = [], _Change] = useUncontrolled({ value: data, onChange })

        const { classes } = useStyles(
            { clearable: _value.length > 0 && clearable, focused: opened, size, focusOutLine, disabled },
            { name: 'Cascade', styles }
        )

        const flatOptions = useMemo(() => {
            function getChildOption(option: CascadeOption, list: CascadeOption[]) {
                const { value, label, path, labelPath, children = [] } = option
                let arrList = list
                for (const child of children) {
                    const newChild = { ...child, path: `${path}/${child.value}`, labelPath: `${labelPath}/${child.label}` }
                    arrList = [...arrList, newChild, ...getChildOption(newChild, [])]
                }
                return arrList
            }
            return options.reduce<CascadeOption[]>((pre, cur) => {
                const newOption = { ...cur, path: cur.value, labelPath: cur.label }
                return [...pre, newOption, ...getChildOption(newOption, [])]
            }, [])
        }, [options])

        const handleClear = useCallback(
            (event: React.MouseEvent<HTMLSpanElement>) => {
                event.stopPropagation()
                _Change([])
                onClear?.(event)
            },
            [_Change, onClear]
        )

        const handleRemove = useCallback(
            (val: string) => {
                const newValue = _value.filter(id => {
                    return val !== id
                })
                _Change(newValue)
            },
            [_Change, _value]
        )

        const handleChange = useCallback(
            (v: CascadeValue) => {
                _Change(v)
                if (!multiple) {
                    toggle(false)
                }
            },
            [_Change, multiple, toggle]
        )

        return (
            <Box ref={ref} className={clsx(className, classes.root)} style={style}>
                <Popover opened={opened} width={popoverWidth} onChange={toggle} withinPortal={withinPortal} disabled={disabled}>
                    <Popover.Target>
                        <Box className={classes.target}>
                            <Flex className={classes.targetWrapper}>
                                {_value.length === 0 && <Box className={classes.placeholder}>{placeholder}</Box>}

                                {_value.map((v, index) => {
                                    const option = flatOptions?.find(option => option.value === v)
                                    return (
                                        option && (
                                            <Tooltip title={option.labelPath} key={v}>
                                                <Box className={classes.tag}>
                                                    <Text>{option.labelPath}</Text>
                                                    <IconFont
                                                        type="Close"
                                                        onClick={() => {
                                                            handleRemove(option.value)
                                                        }}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        )
                                    )
                                })}
                            </Flex>
                            <Box className={classes.iconWrapper}>
                                <IconFont type={downIcon} size={16} />
                                <IconFont onClick={handleClear} className={classes.clearIcon} type="Close" />
                            </Box>
                        </Box>
                    </Popover.Target>
                    <Popover.Dropdown compact>
                        <CascadeList
                            data={_value}
                            onChange={handleChange}
                            multiple={multiple}
                            columnWidth={columnWidth}
                            searchable={searchable}
                            selectionMode={selectionMode}
                            options={options}
                            allSelected={allSelected}
                            {...listProps}
                        />
                    </Popover.Dropdown>
                </Popover>
            </Box>
        )
    }
)
